/**
 * Runtime theme switcher. Writes CSS variables on `:root` so Tailwind's
 * `bg-brand-500` etc. resolve to the active preset, and toggles a
 * `data-theme="dark"` attribute on the html node for dark mode.
 *
 * Persisted to localStorage so first paint after reload uses the user's
 * choice — there's a tiny inline script in `main.tsx` that runs before
 * React hydrates to avoid a flash of unstyled brand.
 */

import { DEFAULT_PRESET, PRESETS, type ThemePresetId } from './presets';

const LS_PRESET = 'billbaaki.web.theme.preset';
const LS_MODE = 'billbaaki.web.theme.mode';
const LS_CUSTOM = 'billbaaki.web.theme.customRamp';

export type ThemeMode = 'light' | 'dark';

/** Surface + ink ramps that read off the brand palette. Light mode uses
 * white surfaces with brand-tinted lines; dark mode uses brand-900-tinted
 * surfaces. The contract: callers only set the brand preset + mode, and
 * the rest of the UI follows. */
function applyMode(mode: ThemeMode) {
  const root = document.documentElement;
  root.setAttribute('data-theme', mode);
  if (mode === 'dark') {
    root.style.setProperty('--surface', '15 18 23');     // near-black
    root.style.setProperty('--surface-2', '23 27 33');
    root.style.setProperty('--ink', '241 245 249');
    root.style.setProperty('--ink-soft', '148 163 184');
    root.style.setProperty('--line', '38 44 53');
  } else {
    root.style.setProperty('--surface', '255 255 255');
    root.style.setProperty('--surface-2', '249 250 251');
    root.style.setProperty('--ink', '15 23 42');
    root.style.setProperty('--ink-soft', '100 116 139');
    root.style.setProperty('--line', '226 232 240');
  }
  // semantic
  root.style.setProperty('--success', '16 185 129');
  root.style.setProperty('--warn', '245 158 11');
  root.style.setProperty('--danger', '239 68 68');
}

function applyBrandRamp(ramp: Record<number, string>) {
  const root = document.documentElement;
  for (const stop of [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]) {
    root.style.setProperty(`--brand-${stop}`, ramp[stop]);
  }
}

/** Write the active preset's CSS vars onto :root. */
export function applyPreset(id: ThemePresetId) {
  const preset = PRESETS.find((p) => p.id === id) ?? PRESETS[0];
  applyBrandRamp(preset.ramp);
  localStorage.setItem(LS_PRESET, id);
  localStorage.removeItem(LS_CUSTOM);
}

/** Apply a fully custom ramp built from a HEX seed. Persists so reloads
 *  keep the custom palette. */
export function applyCustomRamp(ramp: Record<number, string>) {
  applyBrandRamp(ramp);
  localStorage.setItem(LS_CUSTOM, JSON.stringify(ramp));
  localStorage.removeItem(LS_PRESET);
}

export function setMode(mode: ThemeMode) {
  applyMode(mode);
  localStorage.setItem(LS_MODE, mode);
}

export function getMode(): ThemeMode {
  return (localStorage.getItem(LS_MODE) as ThemeMode) ?? 'light';
}

export function getPreset(): ThemePresetId {
  return (localStorage.getItem(LS_PRESET) as ThemePresetId) ?? DEFAULT_PRESET;
}

export function getCustomRamp(): Record<number, string> | null {
  const raw = localStorage.getItem(LS_CUSTOM);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Inline-bootstrap fn — called once before React mounts so the first paint
 *  already has the right palette. */
export function bootstrapTheme() {
  applyMode(getMode());
  const custom = getCustomRamp();
  if (custom) {
    applyBrandRamp(custom);
  } else {
    applyPreset(getPreset());
  }
}

/* ──────────────── HEX → ramp helper for the custom picker ─────────── */

/** Convert "#10B981" or "10B981" → [r,g,b]. Returns null on invalid. */
export function hexToRgb(hex: string): [number, number, number] | null {
  const h = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Given a seed RGB, build a 10-stop ramp by mixing toward white (lighter)
 *  and toward black (darker). Cheap-but-consistent so the user gets a
 *  pleasant palette from a single colour pick. */
export function rampFromHex(hex: string): Record<number, string> | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const stops: Record<number, [number, number, number]> = {
    50: mix(rgb, [255, 255, 255], 0.92),
    100: mix(rgb, [255, 255, 255], 0.85),
    200: mix(rgb, [255, 255, 255], 0.70),
    300: mix(rgb, [255, 255, 255], 0.50),
    400: mix(rgb, [255, 255, 255], 0.25),
    500: rgb,
    600: mix(rgb, [0, 0, 0], 0.15),
    700: mix(rgb, [0, 0, 0], 0.30),
    800: mix(rgb, [0, 0, 0], 0.45),
    900: mix(rgb, [0, 0, 0], 0.60),
  };
  return Object.fromEntries(
    Object.entries(stops).map(([k, [r, g, b]]) => [k, `${r} ${g} ${b}`]),
  );
}

function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}
